import { useMutation } from '@tanstack/react-query';
import { CircleAlert } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionDto } from '@/Domain/api/Action/dto/Action.dto';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

type Props = {
  actionId: number;
};

export const RemoveActionModal = ({ actionId }: Props) => {
  const { t } = useTranslation('actions_page');
  const { isOpen, toggleModal } = useModalStore();

  const name = `${MODAL_NAMES.REMOVE_ACTION_MODAL_NAME}_${actionId}`;

  const { mutate, isPending } = useMutation<ActionDto, unknown, number>({
    mutationKey: [QueryKey.actions.basic.delete],
  });

  return (
    <AlertDialog open={isOpen(name)} onOpenChange={() => !isPending && toggleModal(name)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <CircleAlert className="size-4 bg-red-100 rounded-lg text-red-500 p-1.5 box-content" />
            {t('modal.delete_action.title')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            <Trans i18nKey="modal.delete_action.description" t={t} components={{ bold: <b /> }} />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel asChild disabled={isPending}>
            <Button variant="secondary" disabled={isPending}>
              {t('modal.delete_action.button.cancel')}
            </Button>
          </AlertDialogCancel>
          <Button
            variant="danger"
            disabled={isPending}
            onClick={() => mutate(actionId)}
            data-testid="action-danger-delete-button"
          >
            {t('modal.delete_action.button.delete')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
